import { useRef } from 'react'
import { type UploadedFile } from './UploadedFileCard'

interface Props {
  uploadedFile: UploadedFile
  onUpload: (file: File) => void
  uploading: boolean
  onExtract: () => void
  extracting: boolean
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatContentType(ct: string) {
  return ct.split('/').pop()?.toUpperCase() ?? ct
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' • ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  )
}

export default function FileMetadataPanel({ uploadedFile, onUpload, uploading, onExtract, extracting }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onUpload(file)
    e.target.value = ''
  }

  return (
    <div className="w-full sm:w-52 shrink-0 flex flex-col p-5 gap-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand/10 border border-brand/20 flex items-center justify-center shrink-0">
          <svg className="w-4.5 h-4.5 text-brand" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-gray-900 text-sm font-medium truncate">{uploadedFile.filename}</p>
          <p className="text-gray-500 text-xs mt-0.5">
            {formatContentType(uploadedFile.content_type)}
            {uploadedFile.byte_size ? ` • ${formatBytes(uploadedFile.byte_size)}` : ''}
          </p>
        </div>
      </div>

      <div className="h-px bg-gray-200" />

      <div className="space-y-3 text-xs">
        <div>
          <p className="text-gray-400 uppercase tracking-wider text-[10px] font-medium mb-0.5">Uploaded</p>
          <p className="text-gray-700">
            {uploadedFile.created_at ? formatDate(uploadedFile.created_at) : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-400 uppercase tracking-wider text-[10px] font-medium mb-0.5">Uploaded by</p>
          <p className="text-gray-700">jdoe@optimal.com</p>
        </div>
      </div>

      <div className="h-px bg-gray-200" />

      <div className="space-y-2 mt-auto">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleChange}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition-colors disabled:opacity-40 cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {uploading ? 'Uploading…' : 'Replace File'}
        </button>
        <button
          type="button"
          onClick={onExtract}
          disabled={extracting}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-brand hover:bg-brand-dark px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-60 cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
          </svg>
          {extracting ? 'Extracting…' : 'Extract Data'}
        </button>
      </div>
    </div>
  )
}
